import type {
  DtoV1BoltAuditFieldAuditHeader,
  DtoV1BoltAuditIAuditComponent,
} from '~/__generated__/api';
import SingleAccordion from '~/common/components/Accordions/SingleAccordion/SingleAccordion';
import Stack from '~/common/components/Stacks/Stack';

import AuditTable from '../AuditTable';

type SectionedAuditComponent = DtoV1BoltAuditIAuditComponent;
type SectionedAuditTable = DtoV1BoltAuditFieldAuditHeader & {
  loanAction?: string | null;
  maxRowsPerPage?: number | null;
};

type SectionedAuditRendererProps = {
  sections?: DtoV1BoltAuditIAuditComponent[] | null;
};

function isAccordionComponent(component: SectionedAuditComponent): boolean {
  const candidate = component as DtoV1BoltAuditFieldAuditHeader;
  return Array.isArray(candidate?.children);
}

function resolveTableLoanAction(table: SectionedAuditTable) {
  return table.loanAction || table.type || 'Loan Action';
}

export default function SectionedAuditRenderer({
  sections,
}: Readonly<SectionedAuditRendererProps>) {
  if (!sections?.length) {
    return null;
  }

  return (
    <Stack gap={8}>
      {sections.map((component, index) => {
        const componentKey = `sectioned-component-${index}`;

        if (isAccordionComponent(component)) {
          const accordion = component as {
            accordionTitle?: string;
            children?: DtoV1BoltAuditIAuditComponent[] | null;
          };

          return (
            <SingleAccordion
              key={componentKey}
              title={accordion?.accordionTitle || 'Section'}
            >
              <SectionedAuditRenderer sections={accordion?.children} />
            </SingleAccordion>
          );
        }

        const table = component as SectionedAuditTable;
        if (!table.fields?.length) {
          return null;
        }

        return (
          <Stack key={componentKey} gap={8}>
            <AuditTable
              key={`${componentKey}-table`}
              data={table.fields}
              section={table}
              maxPageSize={table.maxRowsPerPage}
              headers={{
                loanAction: resolveTableLoanAction(table),
              }}
              sx={{ tableLayout: 'fixed' }}
            />
          </Stack>
        );
      })}
    </Stack>
  );
}
